package org.catharsis.spendtel.feature.presentation.expensereport

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ExpandLess
import androidx.compose.material.icons.filled.ExpandMore
import androidx.compose.material.icons.outlined.AutoAwesome
import androidx.compose.material.icons.outlined.Category
import androidx.compose.material.icons.outlined.Favorite
import androidx.compose.material.icons.outlined.Psychology
import androidx.compose.material.icons.outlined.ReceiptLong
import androidx.compose.material.icons.outlined.Store
import androidx.compose.material.icons.outlined.Timeline
import androidx.compose.material.icons.outlined.TrendingUp
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.catharsis.spendtel.feature.data.ChangeItem
import org.catharsis.spendtel.feature.data.ExpenseReport
import org.catharsis.spendtel.feature.data.FinancialPersonality
import org.catharsis.spendtel.feature.data.LifeCategory
import org.catharsis.spendtel.feature.data.MerchantInsight
import org.catharsis.spendtel.feature.data.MonthlyStory
import org.catharsis.spendtel.feature.data.ReportSummary
import org.catharsis.spendtel.feature.data.SpendingStory
import org.catharsis.spendtel.feature.data.TimelineEntry
import org.catharsis.spendtel.feature.data.Transaction
import org.catharsis.spendtel.feature.data.WhatChanged
import org.catharsis.spendtel.feature.data.formatCurrency
import org.catharsis.spendtel.feature.data.formatIsoDate
import org.catharsis.spendtel.feature.data.formatPeriodRange

@Composable
fun ExpenseReportStoryContent(
    report: ExpenseReport,
    modifier: Modifier = Modifier,
    separatedModules: Boolean = false,
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(if (separatedModules) 32.dp else 28.dp),
    ) {
        report.monthlyStory?.let { story ->
            StoryContentModule(separatedModules) {
                MonthlyStoryHero(
                    story = story,
                    summary = report.summary,
                )
            }
        } ?: report.summary?.let { summary ->
            StoryContentModule(separatedModules) {
                PeriodHero(summary = summary)
            }
        }

        report.financialPersonality?.let { personality ->
            StoryContentModule(separatedModules) {
                FinancialPersonalityHero(personality = personality)
            }
        }

        if (report.highlights.isNotEmpty()) {
            StoryContentModule(separatedModules) {
                HighlightsSection(highlights = report.highlights)
            }
        }

        if (report.equivalences.isNotEmpty()) {
            StoryContentModule(separatedModules) {
                EquivalencesSection(
                    equivalences = report.equivalences,
                    currency = report.dashboardCurrency(),
                )
            }
        }

        if (report.goalInsights.isNotEmpty()) {
            StoryContentModule(separatedModules) {
                GoalInsightsSection(insights = report.goalInsights)
            }
        }

        if (report.spendingStories.isNotEmpty()) {
            StoryContentModule(separatedModules) {
                SpendingStoriesSection(stories = report.spendingStories)
            }
        }

        report.whatChanged?.let { changes ->
            StoryContentModule(separatedModules) {
                WhatChangedSection(changes = changes)
            }
        }

        report.trends?.let { trends ->
            StoryContentModule(separatedModules) {
                ReportTrendsSection(trends = trends)
            }
        }

        if (!report.hasComparisonContent && report.isComplete) {
            StoryContentModule(separatedModules) {
                FirstReportComparisonTeaser()
            }
        }

        report.spendingPatterns?.let { patterns ->
            StoryContentModule(separatedModules) {
                SpendingPatternsSection(
                    patterns = patterns,
                    currency = report.dashboardCurrency(),
                )
            }
        }

        if (report.merchantInsights.isNotEmpty()) {
            StoryContentModule(separatedModules) {
                MerchantInsightsSection(
                    merchants = report.merchantInsights,
                    currency = report.dashboardCurrency(),
                )
            }
        }

        report.moneyLeaks?.let { leaks ->
            StoryContentModule(separatedModules) {
                MoneyLeaksSection(
                    moneyLeaks = leaks,
                    currency = report.dashboardCurrency(),
                )
            }
        }

        if (report.aboutYou.isNotEmpty()) {
            StoryContentModule(separatedModules) {
                AboutYouSection(lines = report.aboutYou)
            }
        }

        if (report.spendingTimeline.isNotEmpty()) {
            StoryContentModule(separatedModules) {
                SpendingTimelineSection(entries = report.spendingTimeline)
            }
        }

        if (report.lifeCategories.isNotEmpty()) {
            StoryContentModule(separatedModules) {
                LifeCategoriesSection(
                    categories = report.lifeCategories,
                    currency = report.dashboardCurrency(),
                )
            }
        }

        if (report.transactions.isNotEmpty()) {
            StoryContentModule(separatedModules) {
                CollapsibleTransactionsSection(
                    transactions = report.transactions,
                    currency = report.dashboardCurrency(),
                )
            }
        }

        if (separatedModules) {
            StoryModuleFooter {
                DiscoverTeaser(
                    text = "Your spending story evolves with every report you add.",
                )
            }
        } else {
            DiscoverTeaser(
                text = "Your spending story evolves with every report you add.",
                modifier = Modifier.padding(top = 4.dp),
            )
        }
    }
}

@Composable
private fun MonthlyStoryHero(
    story: MonthlyStory,
    summary: ReportSummary?,
) {
    GradientHeroCard(gradient = SpendTelGradients.hero) {
        Text(
            text = "YOUR MONTHLY STORY",
            style = MaterialTheme.typography.labelMedium,
            color = Color.White.copy(alpha = 0.72f),
            letterSpacing = 1.2.sp,
        )
        Text(
            text = story.headline,
            style = MaterialTheme.typography.headlineMedium,
            fontWeight = FontWeight.Bold,
            color = Color.White,
            lineHeight = 34.sp,
        )
        summary?.let {
            formatPeriodRange(it.periodStart, it.periodEnd)?.let { period ->
                Text(
                    text = period,
                    style = MaterialTheme.typography.bodyMedium,
                    color = Color.White.copy(alpha = 0.82f),
                )
            }
            Text(
                text = formatCurrency(it.totalSpending, it.currency),
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.SemiBold,
                color = Color.White.copy(alpha = 0.95f),
            )
        }
        if (story.observations.isNotEmpty()) {
            Spacer(modifier = Modifier.height(4.dp))
            Text(
                text = story.observations.first(),
                style = MaterialTheme.typography.bodyLarge,
                color = Color.White.copy(alpha = 0.9f),
                lineHeight = 24.sp,
            )
        }
    }

    if (story.observations.size > 1) {
        Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
            story.observations.drop(1).forEach { observation ->
                QuoteObservation(
                    text = observation,
                    accent = SpendTelReportColors.coral,
                )
            }
        }
    }
}

@Composable
private fun PeriodHero(summary: ReportSummary) {
    GradientHeroCard(gradient = SpendTelGradients.storyWarm) {
        formatPeriodRange(summary.periodStart, summary.periodEnd)?.let { period ->
            Text(
                text = period,
                style = MaterialTheme.typography.titleMedium,
                color = Color.White.copy(alpha = 0.9f),
            )
        }
        Text(
            text = formatCurrency(summary.totalSpending, summary.currency),
            style = MaterialTheme.typography.displaySmall,
            fontWeight = FontWeight.Bold,
            color = Color.White,
        )
        Text(
            text = "Total spending this period",
            style = MaterialTheme.typography.bodyMedium,
            color = Color.White.copy(alpha = 0.85f),
        )
    }
}

@Composable
private fun FinancialPersonalityHero(personality: FinancialPersonality) {
    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
        ReportSectionHeader(
            title = "Your financial personality",
            subtitle = "Share-worthy insight about how you spend",
            accent = SpendTelReportColors.violet,
            icon = Icons.Outlined.Psychology,
        )
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(28.dp))
                .background(SpendTelGradients.personality)
                .padding(24.dp),
        ) {
            Box(
                modifier = Modifier
                    .size(140.dp)
                    .align(Alignment.TopEnd)
                    .offset(x = 30.dp, y = (-30).dp)
                    .clip(CircleShape)
                    .background(Color.White.copy(alpha = 0.1f)),
            )
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                Surface(
                    shape = RoundedCornerShape(999.dp),
                    color = Color.White.copy(alpha = 0.18f),
                ) {
                    Text(
                        text = "PERSONALITY TYPE",
                        modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
                        style = MaterialTheme.typography.labelSmall,
                        color = Color.White.copy(alpha = 0.9f),
                        letterSpacing = 1.sp,
                    )
                }
                Text(
                    text = personality.label,
                    style = MaterialTheme.typography.headlineMedium,
                    fontWeight = FontWeight.Bold,
                    color = Color.White,
                    lineHeight = 34.sp,
                )
                Text(
                    text = personality.description,
                    style = MaterialTheme.typography.bodyLarge,
                    color = Color.White.copy(alpha = 0.92f),
                    lineHeight = 24.sp,
                )
            }
        }
    }
}

@Composable
fun SpendingStoriesSection(
    stories: List<SpendingStory>,
    showHeader: Boolean = true,
) {
    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        if (showHeader) {
            ReportSectionHeader(
                title = "Spending stories",
                subtitle = "Themed chapters from your month",
                accent = SpendTelReportColors.amber,
                icon = Icons.Outlined.AutoAwesome,
            )
        }
        stories.forEachIndexed { index, story ->
            val accent = accentForIndex(index)
            AccentSurfaceCard(accent = accent) {
                Text(
                    text = story.title,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = SpendTelReportColors.ink,
                )
                story.insights.forEach { insight ->
                    QuoteObservation(text = insight, accent = accent)
                }
            }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun WhatChangedSection(changes: WhatChanged) {
    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        ReportSectionHeader(
            title = "What changed?",
            subtitle = "How this month compares to your last story",
            accent = SpendTelReportColors.teal,
            icon = Icons.Outlined.TrendingUp,
        )
        SoftInsightCard(accent = SpendTelReportColors.teal) {
            if (changes.biggestIncreases.isNotEmpty()) {
                ChangeGroup(
                    title = "Biggest increases",
                    items = changes.biggestIncreases,
                    valueColor = SpendTelReportColors.increase,
                )
            }
            if (changes.biggestDecreases.isNotEmpty()) {
                ChangeGroup(
                    title = "Biggest decreases",
                    items = changes.biggestDecreases,
                    valueColor = SpendTelReportColors.decrease,
                )
            }
            if (changes.newHabits.isNotEmpty()) {
                Text(
                    text = "New habits emerging",
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.Bold,
                    color = SpendTelReportColors.ink,
                )
                FlowRow(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    changes.newHabits.forEach { habit ->
                        InsightPill(text = habit, accent = SpendTelReportColors.plum)
                    }
                }
            }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun ChangeGroup(
    title: String,
    items: List<ChangeItem>,
    valueColor: Color,
) {
    Text(
        text = title,
        style = MaterialTheme.typography.titleSmall,
        fontWeight = FontWeight.Bold,
        color = SpendTelReportColors.ink,
    )
    items.forEach { item ->
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = item.label,
                style = MaterialTheme.typography.bodyMedium,
                color = SpendTelReportColors.ink,
                modifier = Modifier.weight(1f),
            )
            Text(
                text = item.change,
                style = MaterialTheme.typography.titleSmall,
                fontWeight = FontWeight.Bold,
                color = valueColor,
            )
        }
    }
}

@Composable
private fun MerchantInsightsSection(
    merchants: List<MerchantInsight>,
    currency: String,
) {
    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        ReportSectionHeader(
            title = "Merchant intelligence",
            subtitle = "Patterns hiding in your favorite places",
            accent = SpendTelReportColors.sky,
            icon = Icons.Outlined.Store,
        )
        merchants.forEachIndexed { index, merchant ->
            val accent = accentForIndex(index + 2)
            Card(
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(20.dp),
                colors = CardDefaults.cardColors(containerColor = Color.White),
                elevation = CardDefaults.cardElevation(defaultElevation = 0.dp),
            ) {
                Column(
                    modifier = Modifier.padding(18.dp),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Text(
                            text = merchant.merchant,
                            style = MaterialTheme.typography.titleSmall,
                            fontWeight = FontWeight.Bold,
                            color = SpendTelReportColors.ink,
                            modifier = Modifier.weight(1f),
                        )
                        MetricBadge(
                            label = "Times Paid",
                            value = merchant.visits.toString(),
                            accent = accent,
                        )
                    }
                    Text(
                        text = formatCurrency(merchant.total, currency),
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.SemiBold,
                        color = accent.darkenForText(),
                    )
                    merchant.averageSpend?.let { average ->
                        Text(
                            text = "Average visit: ${formatCurrency(average, currency)}",
                            style = MaterialTheme.typography.labelLarge,
                            color = SpendTelReportColors.inkMuted,
                        )
                    }
                    merchant.trend?.let { trend ->
                        InsightPill(
                            text = trend.replaceFirstChar { it.titlecase() },
                            accent = accent,
                        )
                    }
                    Text(
                        text = merchant.pattern,
                        style = MaterialTheme.typography.bodyMedium,
                        color = SpendTelReportColors.inkMuted,
                        lineHeight = 22.sp,
                    )
                }
            }
        }
    }
}

@Composable
fun AboutYouSection(
    lines: List<String>,
    showHeader: Boolean = true,
) {
    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        if (showHeader) {
            ReportSectionHeader(
                title = "What your spending says about you",
                subtitle = "Reflections worth sitting with",
                accent = SpendTelReportColors.rose,
                icon = Icons.Outlined.Favorite,
            )
        }
        lines.forEachIndexed { index, line ->
            SoftInsightCard(accent = accentForIndex(index + 4)) {
                Text(
                    text = line,
                    style = MaterialTheme.typography.bodyLarge,
                    color = SpendTelReportColors.ink.copy(alpha = 0.9f),
                    lineHeight = 26.sp,
                )
            }
        }
    }
}

@Composable
fun SpendingTimelineSection(
    entries: List<TimelineEntry>,
    showHeader: Boolean = true,
) {
    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        if (showHeader) {
            ReportSectionHeader(
                title = "Spending timeline",
                subtitle = "Your month as a narrative arc",
                accent = SpendTelReportColors.indigo,
                icon = Icons.Outlined.Timeline,
            )
        }
        Card(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(22.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
        ) {
            Column(
                modifier = Modifier.padding(20.dp),
                verticalArrangement = Arrangement.spacedBy(4.dp),
            ) {
                entries.forEachIndexed { index, entry ->
                    TimelineNode(
                        accent = SpendTelReportColors.indigo,
                        isLast = index == entries.lastIndex,
                    ) {
                        Text(
                            text = formatIsoDate(entry.date),
                            style = MaterialTheme.typography.labelLarge,
                            fontWeight = FontWeight.Bold,
                            color = SpendTelReportColors.indigo.darkenForText(),
                        )
                        Text(
                            text = entry.narrative,
                            style = MaterialTheme.typography.bodyMedium,
                            color = SpendTelReportColors.inkMuted,
                            lineHeight = 22.sp,
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun LifeCategoriesSection(
    categories: List<LifeCategory>,
    currency: String,
    showHeader: Boolean = true,
) {
    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        if (showHeader) {
            ReportSectionHeader(
                title = "Life categories",
                subtitle = "Where your money meets your life",
                accent = SpendTelReportColors.mint,
                icon = Icons.Outlined.Category,
            )
        }
        categories.forEach { category ->
            val accent = accentForLabel(category.name)
            AccentSurfaceCard(accent = accent) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Text(
                        text = category.name,
                        style = MaterialTheme.typography.titleSmall,
                        fontWeight = FontWeight.Bold,
                        color = SpendTelReportColors.ink,
                        modifier = Modifier.weight(1f),
                    )
                    Text(
                        text = formatCurrency(category.amount, currency),
                        style = MaterialTheme.typography.titleSmall,
                        fontWeight = FontWeight.Bold,
                        color = accent.darkenForText(),
                    )
                }
                Text(
                    text = category.insight,
                    style = MaterialTheme.typography.bodyMedium,
                    color = SpendTelReportColors.inkMuted,
                    lineHeight = 22.sp,
                )
            }
        }
    }
}

@Composable
fun CollapsibleTransactionsSection(
    transactions: List<Transaction>,
    currency: String,
) {
    var expanded by remember { mutableStateOf(false) }

    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        ReportSectionHeader(
            title = "All activity",
            subtitle = "The receipts behind the story",
            accent = SpendTelReportColors.inkMuted,
            icon = Icons.Outlined.ReceiptLong,
        )
        Card(
            modifier = Modifier
                .fillMaxWidth()
                .clickable { expanded = !expanded },
            shape = RoundedCornerShape(20.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
        ) {
            Column(
                modifier = Modifier.padding(18.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Text(
                        text = if (expanded) "Hide activity" else "See all activity (${transactions.size})",
                        style = MaterialTheme.typography.titleSmall,
                        fontWeight = FontWeight.SemiBold,
                        color = SpendTelReportColors.ink,
                    )
                    Icon(
                        imageVector = if (expanded) Icons.Filled.ExpandLess else Icons.Filled.ExpandMore,
                        contentDescription = null,
                        tint = SpendTelReportColors.mint,
                    )
                }
                AnimatedVisibility(visible = expanded) {
                    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                        transactions.forEach { transaction ->
                            TransactionRow(transaction = transaction, currency = currency)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun TransactionRow(transaction: Transaction, currency: String) {
    val accent = accentForLabel(transaction.lifeCategory)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(14.dp))
            .background(accent.copy(alpha = 0.06f))
            .padding(12.dp),
        horizontalArrangement = Arrangement.spacedBy(12.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(
            modifier = Modifier.weight(1f),
            verticalArrangement = Arrangement.spacedBy(4.dp),
        ) {
            Text(
                text = transaction.merchant,
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                color = SpendTelReportColors.ink,
            )
            Text(
                text = "${transaction.lifeCategory} · ${formatIsoDate(transaction.date)}",
                style = MaterialTheme.typography.labelSmall,
                color = SpendTelReportColors.inkMuted,
            )
        }
        Text(
            text = formatCurrency(transaction.amount, currency),
            style = MaterialTheme.typography.bodyMedium,
            fontWeight = FontWeight.Bold,
            color = accent.darkenForText(),
        )
    }
}

@Composable
fun ExpenseReportProcessingCard(
    status: String,
    modifier: Modifier = Modifier,
) {
    SoftInsightCard(accent = SpendTelReportColors.violet, modifier = modifier) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            CircularProgressIndicator(
                color = SpendTelReportColors.violet,
                strokeWidth = 3.dp,
                modifier = Modifier.size(40.dp),
            )
            Text(
                text = when (status.lowercase()) {
                    "parsing" -> "Crafting your spending story"
                    else -> "Preparing your financial personality"
                },
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = SpendTelReportColors.ink,
            )
            Text(
                text = reportStatusMessage(status),
                style = MaterialTheme.typography.bodyMedium,
                color = SpendTelReportColors.inkMuted,
                lineHeight = 22.sp,
            )
        }
    }
}

@Composable
fun ExpenseReportFailedCard(
    errorMessage: String? = null,
    modifier: Modifier = Modifier,
) {
    SoftInsightCard(accent = SpendTelReportColors.increase, modifier = modifier) {
        Text(
            text = "We couldn't finish your story",
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.Bold,
            color = SpendTelReportColors.ink,
        )
        Text(
            text = errorMessage?.takeIf { it.isNotBlank() }
                ?: "Try uploading your statements again and we'll take another look.",
            style = MaterialTheme.typography.bodyMedium,
            color = SpendTelReportColors.inkMuted,
            lineHeight = 22.sp,
        )
    }
}

@Composable
fun ExpenseReportLegacyFallback(modifier: Modifier = Modifier) {
    SoftInsightCard(accent = SpendTelReportColors.amber, modifier = modifier) {
        Text(
            text = "This report uses an older format",
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.Bold,
            color = SpendTelReportColors.ink,
        )
        Text(
            text = "Create a new report to unlock your conversational spending story and financial personality.",
            style = MaterialTheme.typography.bodyMedium,
            color = SpendTelReportColors.inkMuted,
        )
    }
}

private fun Color.darkenForText(): Color {
    return Color(red = red * 0.75f, green = green * 0.75f, blue = blue * 0.75f, alpha = alpha)
}
